class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }
  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }
  getQuery() { return this.input.value.trim(); }
  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) { this.close(true); return; }
    this.getSearchResults(this.searchTerm);
  }
  onFormSubmit(event) { if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault(); }
  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) { this.searchTerm = ''; this.abortController.abort(); this.abortController = new AbortController(); this.closeResults(true); }
  }
  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) this.onChange(); else if (this.getAttribute('results') === 'true') this.open(); else this.getSearchResults(this.searchTerm);
  }
  onFocusOut() { setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }); }
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    if (event.code === 'ArrowUp') this.switchOption('up'); else if (event.code === 'ArrowDown') this.switchOption('down'); else if (event.code === 'Enter') this.selectOption();
  }
  onKeydown(event) { if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault(); }
  updateSearchForTerm(previousTerm, newTerm) {
    const element = this.querySelector('[data-predictive-search-search-for-text]');
    const text = element?.innerText;
    if (!text) return;
    element.innerText = text.replace(previousTerm, newTerm);
  }
  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');
    const visible = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter((element) => element.offsetParent !== null);
    if (moveUp && !selected) return;
    const selectedIndex = selected ? visible.indexOf(selected) : -1;
    let activeIndex = 0;
    if (!moveUp && selected) activeIndex = selectedIndex === visible.length - 1 ? 0 : selectedIndex + 1; else if (moveUp) activeIndex = selectedIndex === 0 ? visible.length - 1 : selectedIndex - 1;
    if (activeIndex === selectedIndex || !visible[activeIndex]) return;
    visible[activeIndex].setAttribute('aria-selected', true);
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', visible[activeIndex].id);
  }
  selectOption() { this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')?.click(); }
  getSearchResults(searchTerm) {
    const effectiveSearchTerm = window.SpotChicSearch?.buildPredictiveQuery(searchTerm) || searchTerm;
    const queryKey = effectiveSearchTerm.toLocaleLowerCase('fr');
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const deferred = this.dispatchSearchUpdateEvent(searchTerm);
      deferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }
    const deferred = this.dispatchSearchUpdateEvent(searchTerm);
    const params = new URLSearchParams({
      q: effectiveSearchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection',
      'resources[options][fields]': 'title,body',
      'resources[options][unavailable_products]': 'hide',
    });
    this.abortController.abort();
    this.abortController = new AbortController();
    fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal: this.abortController.signal })
      .then((response) => { if (!response.ok) { this.close(); throw new Error(response.status); } return response.text(); })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section missing');
        const markup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => { instance.cachedResults[queryKey] = markup; });
        this.renderSearchResults(markup);
        deferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) { deferred?.reject(error); return; }
        deferred?.reject(error);
        this.close();
        console.error(error);
      });
  }
  getTotalResultCount() { return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults, 10) || 0; }
  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;
    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: deferred.promise }));
    return deferred;
  }
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }
  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
  }
  renderSearchResults(markup) { this.predictiveSearchResults.innerHTML = markup; this.setAttribute('results', true); this.setLiveRegionResults(); this.open(); }
  setLiveRegionResults() { this.removeAttribute('loading'); this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || ''); }
  getResultsMaxHeight() { this.resultsMaxHeight = window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0); return this.resultsMaxHeight; }
  open() { this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`; this.setAttribute('open', true); this.input.setAttribute('aria-expanded', true); this.isOpen = true; }
  close(clearSearchTerm = false) { this.closeResults(clearSearchTerm); this.isOpen = false; }
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) { this.input.value = ''; this.removeAttribute('results'); }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}
customElements.define('predictive-search', PredictiveSearch);
